/**
 * Fleet API — the registry plus each vehicle's service log, issue reports, and
 * photos. Photos reuse the shared attachments pipeline (multipart + signed URLs).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Attachment,
  Vehicle,
  VehicleCreateInput,
  VehicleIssue,
  VehicleIssueCreateInput,
  VehicleIssueUpdateInput,
  VehicleServiceLog,
  VehicleServiceLogCreateInput,
  VehicleServiceLogUpdateInput,
  VehicleUpdateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { downscaleImage } from '@/lib/imageDownscale';

// ---- Registry ---------------------------------------------------------------
export function useVehicles(includeInactive = false) {
  return useQuery<{ data: Vehicle[] }>({
    queryKey: ['vehicles', includeInactive],
    queryFn: () =>
      apiCall<{ data: Vehicle[] }>(`/vehicles${includeInactive ? '?include_inactive=true' : ''}`),
  });
}

export function useVehicle(id: string | undefined) {
  return useQuery<Vehicle>({
    queryKey: ['vehicle', id],
    enabled: !!id,
    queryFn: () => apiCall<Vehicle>(`/vehicles/${id}`),
  });
}

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation<Vehicle, Error, VehicleCreateInput>({
    mutationFn: (body) => apiCall<Vehicle>('/vehicles', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });
}

export function useUpdateVehicle() {
  const qc = useQueryClient();
  return useMutation<Vehicle, Error, { id: string; body: VehicleUpdateInput }>({
    mutationFn: ({ id, body }) => apiCall<Vehicle>(`/vehicles/${id}`, { method: 'PATCH', body }),
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle', v.id] });
    },
  });
}

// ---- Photos -----------------------------------------------------------------
async function uploadPhoto(url: string, file: File): Promise<Attachment> {
  const fd = new FormData();
  fd.append('file', file.type.startsWith('image/') ? await downscaleImage(file) : file);
  return apiCall<Attachment>(url, { method: 'POST', body: fd });
}

export const uploadVehiclePhoto = (vehicleId: string, file: File) =>
  uploadPhoto(`/vehicles/${vehicleId}/photos`, file);
export const uploadServiceLogPhoto = (serviceLogId: string, file: File) =>
  uploadPhoto(`/service-logs/${serviceLogId}/photos`, file);
export const uploadIssuePhoto = (issueId: string, file: File) =>
  uploadPhoto(`/vehicle-issues/${issueId}/photos`, file);

export const deletePhoto = (attachmentId: string) =>
  apiCall<void>(`/attachments/${attachmentId}`, { method: 'DELETE' });

export function useVehiclePhotos(vehicleId: string | undefined) {
  return useQuery<{ data: Attachment[] }>({
    queryKey: ['vehicle-photos', vehicleId],
    enabled: !!vehicleId,
    queryFn: () => apiCall<{ data: Attachment[] }>(`/vehicles/${vehicleId}/photos`),
  });
}

// ---- Service log ------------------------------------------------------------
export function useVehicleServiceLogs(vehicleId: string | undefined) {
  return useQuery<{ data: VehicleServiceLog[] }>({
    queryKey: ['vehicle-service-logs', vehicleId],
    enabled: !!vehicleId,
    queryFn: () => apiCall<{ data: VehicleServiceLog[] }>(`/vehicles/${vehicleId}/service-logs`),
  });
}

export function useCreateServiceLog(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<VehicleServiceLog, Error, VehicleServiceLogCreateInput>({
    mutationFn: (body) =>
      apiCall<VehicleServiceLog>(`/vehicles/${vehicleId}/service-logs`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-service-logs', vehicleId] }),
  });
}

export function useUpdateServiceLog(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<VehicleServiceLog, Error, { id: string; body: VehicleServiceLogUpdateInput }>({
    mutationFn: ({ id, body }) =>
      apiCall<VehicleServiceLog>(`/service-logs/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-service-logs', vehicleId] }),
  });
}

export function useDeleteServiceLog(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiCall<void>(`/service-logs/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-service-logs', vehicleId] }),
  });
}

// ---- Issues -----------------------------------------------------------------
export function useVehicleIssues(vehicleId: string | undefined) {
  return useQuery<{ data: VehicleIssue[] }>({
    queryKey: ['vehicle-issues', vehicleId],
    enabled: !!vehicleId,
    queryFn: () => apiCall<{ data: VehicleIssue[] }>(`/vehicles/${vehicleId}/issues`),
  });
}

/** Fleet-wide open issues — supervisor dashboard / Home badge. */
export function useFleetOpenIssues(enabled = true) {
  return useQuery<{ data: VehicleIssue[] }>({
    queryKey: ['fleet-open-issues'],
    enabled,
    queryFn: () => apiCall<{ data: VehicleIssue[] }>(`/vehicle-issues?status=open`),
    staleTime: 30_000,
  });
}

export function useCreateVehicleIssue(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<VehicleIssue, Error, VehicleIssueCreateInput>({
    mutationFn: (body) =>
      apiCall<VehicleIssue>(`/vehicles/${vehicleId}/issues`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-issues', vehicleId] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });
      qc.invalidateQueries({ queryKey: ['fleet-open-issues'] });
    },
  });
}

export function useUpdateVehicleIssue(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<VehicleIssue, Error, { id: string; body: VehicleIssueUpdateInput }>({
    mutationFn: ({ id, body }) =>
      apiCall<VehicleIssue>(`/vehicle-issues/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-issues', vehicleId] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });
      qc.invalidateQueries({ queryKey: ['fleet-open-issues'] });
    },
  });
}
